import React, { useState, useEffect } from "react";
import { Grid } from "@material-ui/core";
import { Controller, useWatch } from "react-hook-form";
import { EventNotification } from "../../domain/EventNotification";
import { SectionProps } from "./SectionProps";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { Select } from "@ofqual/portal-components/components/Select";
import {
  eventTypeService,
  eventSubTypeService,
  tagsService,
} from "../../services";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { fieldLabels } from "./fieldLabels";
import { Model } from "@ofqual/portal-core/core/types";
import { toGroupedList } from "@ofqual/portal-core/core/utils/listHelpers";
import { ItemType } from "@ofqual/portal-components/components/types";
import { Error } from "../common/Error";

export const SummaryOfqual: React.FC<SectionProps<EventNotification>> = ({
  control,
  errors,
  disabled,
  lookupTracker,
  registerCustomError,
}) => {
  const [eventTypeLookup, tagsLookups] = useLookup(eventTypeService, {
    service: tagsService,
    id: "id",
    label: "tag",
  });

  const { eventType } = useWatch<{ eventType: number }>({
    control,
    name: ["eventType"],
  });

  const [subTypes, setSubTypes] = useState<
    {
      id: number | null;
      label: string;
    }[]
  >([]);

  useEffect(() => {
    if (eventType) {
      setSubTypes([]);
      eventSubTypeService
        .getAll({ parentId: eventType })
        .then((items) => items.filter((x) => x.active))
        .then((results) =>
          setSubTypes(results.map((x: Model) => ({ id: x.id, label: x.label })))
        );
    }
  }, [eventType]);

  const groupedTagsLookup = toGroupedList(tagsLookups, {
    id: "id",
    label: "tag",
    group: "tagCategory",
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="caseSummary"
          render={({ onChange, value }) => (
            <TextField
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("caseSummary", false);
                }
                onChange(e);
              }}
              label={fieldLabels.caseSummary}
              value={value}
              readOnly={disabled}
              multiline
              data-test="case-summary"
            />
          )}
        />
        {errors.caseSummary && (
          <Error data-test="caseSummary-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={6}>
        <Controller
          control={control}
          name="eventType"
          render={({ onChange, value }) => (
            <Select
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("eventType", false);
                }
                onChange(e);
              }}
              label={fieldLabels.eventType}
              value={value}
              readOnly={disabled}
              data-test="event-type"
              lookupTracker={lookupTracker}
            >
              {eventTypeLookup}
            </Select>
          )}
        />
        {errors.eventType && (
          <Error data-test="eventType-validation">This field is required</Error>
        )}
      </Grid>
      <Grid item xs={6}>
        <Controller
          control={control}
          name="eventSubType"
          render={({ onChange, value }) => (
            <Select
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("eventSubType", false);
                }
                onChange(e);
              }}
              label={fieldLabels.eventSubType}
              disabled={!eventType && !disabled}
              readOnly={disabled}
              value={value}
              data-test="event-sub-type"
              lookupTracker={lookupTracker}
            >
              {subTypes}
            </Select>
          )}
        />
        {errors.eventSubType && (
          <Error data-test="eventSubType-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="ofqualEventManagementActivities"
          render={({ onChange, value }) => (
            <TextField
              onChange={(e) => {
                if (e.target.value !== null) {
                  registerCustomError("ofqualEventManagementActivities", false);
                }
                onChange(e);
              }}
              label={fieldLabels.ofqualEventManagementActivities}
              readOnly={disabled}
              value={value}
              multiline
              data-test="ofqual-event-management-activities"
            />
          )}
        />
        {errors.ofqualEventManagementActivities && (
          <Error data-test="ofqualEventManagementActivities-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="resourceTags"
          as={
            <Select
              selectVariant="checkboxes"
              label={fieldLabels.resourceTags}
              datatest="tags"
              disabled={disabled}
              lookupTracker={lookupTracker}
            >
              {groupedTagsLookup as ItemType[]}
            </Select>
          }
        />
      </Grid>
    </Grid>
  );
};
